import {differenceInSeconds} from 'date-fns';
import Moment from 'moment';
import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';

import UpdateViewerMutation from '../../../../../../mutations/update_viewer_mutation.modern.js';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import Util from '../../../../util/util';
import type {TimeRegistrationMutationInput} from './logic/TimeRegistrationModalMutation';
import {createTimeRegistration} from './logic/TimeRegistrationModalMutation';
import TimeRegistrationModalQueryWrapper from './query-wrappers/TimeRegistrationModalQueryWrapper';
import {toEntityId} from './util/TimeRegistrationModalUtil';

import type {TimeRegistrationModalTimer_viewer} from './__generated__/TimeRegistrationModalTimer_viewer.graphql';
import type {TimeRegistrationEntityId} from './types/TimeRegistrationEntityId';
import type {TimeRegistrationModalInfo} from './types/TimeRegistrationModalInfo';

type GetHeaderTextInput = {preventedDelete: boolean; shouldRelaunchTimer: boolean};
function getHeaderText({preventedDelete, shouldRelaunchTimer}: GetHeaderTextInput): string {
	if (preventedDelete) {
		return 'Timer prevented delete';
	}

	if (shouldRelaunchTimer) {
		return 'Save timer data';
	}

	return 'Timer entry';
}

type GetSearchTextInput = {shouldRelaunchTimer: boolean};
function getSearchFieldText({shouldRelaunchTimer}: GetSearchTextInput): string | undefined {
	if (shouldRelaunchTimer) {
		return 'Would you like to save the timer you started on the task below?';
	}

	return undefined;
}

type GetInfoTextInput = {preventedDelete: boolean; shouldRelaunchTimer: boolean};

function getInfoText({preventedDelete, shouldRelaunchTimer}: GetInfoTextInput): TimeRegistrationModalInfo {
	return {
		headerText: getHeaderText({preventedDelete, shouldRelaunchTimer}),
		searchFieldText: getSearchFieldText({shouldRelaunchTimer}),
		creativeButtonText: 'Save',
		secondaryButtonText: 'Reset Timer',
	};
}

type ResetOrRelaunchTimerInput = {shouldRelaunchTimer: boolean; timerActionTaskId: string | null};
function resetOrRelaunchTimer({shouldRelaunchTimer, timerActionTaskId}: ResetOrRelaunchTimerInput): void {
	trackEvent('Timer', `${shouldRelaunchTimer ? 'Restarted' : 'Reset'}`);
	Util.CommitMutation(UpdateViewerMutation, {
		timerStartDate: shouldRelaunchTimer ? Moment.utc().format() : null,
		timerEndDate: null,
		timerTaskId: shouldRelaunchTimer ? timerActionTaskId : null,
	});
}

type CreateTimerTimeRegistrationInput = {
	timeRegistrationMutationInput: TimeRegistrationMutationInput;
	shouldRelaunchTimer: boolean;
	timerActionTaskId: string | null;
};
function createTimerTimeRegistration({
	timeRegistrationMutationInput,
	shouldRelaunchTimer,
	timerActionTaskId,
}: CreateTimerTimeRegistrationInput): void {
	createTimeRegistration(timeRegistrationMutationInput);
	resetOrRelaunchTimer({shouldRelaunchTimer, timerActionTaskId});
}

type ResetTimerInput = {
	shouldRelaunchTimer: boolean;
	timerActionTaskId: string | null;
	closeModal: () => void;
};
function resetTimer({shouldRelaunchTimer, timerActionTaskId, closeModal}: ResetTimerInput): void {
	resetOrRelaunchTimer({shouldRelaunchTimer, timerActionTaskId});
	closeModal();
}

type GetEntityIdFromTimerInput = {timerActionTaskId: string | null; timerTaskId: string | null};
// Determine what entity the time from the timer should be added to.
// If the timer was originally started on a task (the task from timerTaskId), then that task should always be the target, even if the timer was started/stopped on a different task (the task from timerActionTaskId).
// If the timer start action is on a different task than the one that ends up as the time registration, the timer should be started on that task after the time registration is created.
function getEntityIdFromTimer({timerActionTaskId, timerTaskId}: GetEntityIdFromTimerInput): TimeRegistrationEntityId | null {
	const taskToRegisterOn = timerTaskId || timerActionTaskId;
	return taskToRegisterOn ? toEntityId({taskId: taskToRegisterOn, projectId: null, idleTimeId: null}) : null;
}

type GetTimerMinutesFromTimerDatesInput = {
	timerStartDateString: string | null;
	timerEndDateString: string | null;
};
function getTimerMinutesFromTimerDates({timerStartDateString, timerEndDateString}: GetTimerMinutesFromTimerDatesInput): number {
	if (!timerStartDateString) return 0;

	const timerStartDate = new Date(timerStartDateString);
	const timerEndDate = timerEndDateString ? new Date(timerEndDateString) : new Date();
	const diffInSeconds = differenceInSeconds(timerEndDate, timerStartDate);
	// convert to minutes and round up to next minute
	return Math.ceil(diffInSeconds / 60);
}

// Component
export type TimeRegistrationModalTimerBaseProps = {
	timerActionTaskId: string | null; // The task which the timer action (start / stop) was used on.
	preventedDelete: boolean; // Timer was invoked as the result of a user trying to delete the task the timer was started on.
	closeModal: () => void;
};
interface TimeRegistrationModalTimerProps extends TimeRegistrationModalTimerBaseProps {
	viewer: TimeRegistrationModalTimer_viewer;
}
function TimeRegistrationModalTimer({viewer, timerActionTaskId, preventedDelete, closeModal}: TimeRegistrationModalTimerProps) {
	const {timerStartDate, timerEndDate, actualPersonId} = viewer;
	const timerTaskId = viewer.timerTask ? viewer.timerTask.id : null;
	const entityId = getEntityIdFromTimer({timerActionTaskId, timerTaskId: timerTaskId});
	// Timer action was performed on a different task than the existing timer task.
	const shouldRelaunchTimer = timerTaskId && timerActionTaskId ? timerActionTaskId !== timerTaskId : false;

	const timerMinutes = getTimerMinutesFromTimerDates({
		timerStartDateString: timerStartDate,
		timerEndDateString: timerEndDate,
	});

	const initialState = {
		minutes: timerMinutes,
	};

	return (
		<TimeRegistrationModalQueryWrapper
			personId={actualPersonId}
			entityId={entityId}
			projectId={undefined}
			infoText={getInfoText({preventedDelete, shouldRelaunchTimer})}
			initialState={initialState}
			shouldValidateEntity
			showValidationError
			callbackPositive={timeRegistrationMutationInput =>
				createTimerTimeRegistration({timeRegistrationMutationInput, shouldRelaunchTimer, timerActionTaskId})
			}
			resetTimer={() => resetTimer({shouldRelaunchTimer: shouldRelaunchTimer, timerActionTaskId, closeModal})}
			closeModal={closeModal}
		/>
	);
}

export const TimeRegistrationModalTimerQuery = graphql`
	query TimeRegistrationModalTimer_Query {
		viewer {
			actualPersonId
			component(name: "time_registration_modal_timer")
			...TimeRegistrationModalTimer_viewer
		}
	}
`;

export default createFragmentContainer(TimeRegistrationModalTimer, {
	viewer: graphql`
		fragment TimeRegistrationModalTimer_viewer on Viewer {
			actualPersonId
			timerStartDate
			timerEndDate
			timerTask {
				id
			}
		}
	`,
});
